import { useMemo } from 'react';
import {
  SpreadsheetConfig,
  SpreadsheetConfigConstructorParams,
} from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { WaterfallFundSummaryRowConfig } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig';
import { AggregatedFundOwnershipDetail } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  WATERFALL_FUND_SUMMARY_COLUMNS,
  WATERFALL_FUND_SUMMARY_COLUMNS_WITH_LOAN,
  WATERFALL_FUND_SUMMARY_SHEET_NAME,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/UseWaterfallFundSummarySheetConfig.constants';
import { UseWaterfallFundSummarySheetConfigProps } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig.props';
import { waterfallFundSummaryParser } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/WaterfallFundSummaryParser/WaterfallFundSummaryParser';
import {
  WaterfallFundSummaryColumn,
  WaterfallFundSummaryTableData,
} from './useWaterfallFundSummarySheetConfig.types';

export const useWaterfallFundSummarySheetConfig = ({
  captable,
  fundList,
  childrenCompanies,
  calculatedEquityValue,
  fundInvestmentSheetNames,
  historicalInvestedCapitalTotals,
}: UseWaterfallFundSummarySheetConfigProps) => {
  const hasLoanValues = useMemo(() => {
    if (!captable) {
      return false;
    }
    return (captable.fund_ownership?.fund_ownership_detail ?? []).some(d => Number(d.loan_value ?? '0') > 0);
  }, [captable]);

  const rowConfig = useMemo(() => {
    if (!captable || !calculatedEquityValue) {
      return [];
    }
    return WaterfallFundSummaryRowConfig({
      captable,
      historicalInvestedCapitalTotals,
    });
  }, [captable, calculatedEquityValue, historicalInvestedCapitalTotals]);

  const tableData = useMemo<WaterfallFundSummaryTableData | undefined>(() => {
    if (!captable) {
      return undefined;
    }

    return {
      fundList,
      childrenCompanies,
      captable,
      fundInvestmentSheetNames,
    };
  }, [fundList, childrenCompanies, captable, fundInvestmentSheetNames]);

  const sheetConfig = useMemo(() => {
    if (rowConfig.length > 0 && tableData) {
      type FundSummaryConfigParams = SpreadsheetConfigConstructorParams<
        object,
        WaterfallFundSummaryColumn,
        WaterfallFundSummaryTableData,
        AggregatedFundOwnershipDetail
      >;
      const configParams = {
        name: WATERFALL_FUND_SUMMARY_SHEET_NAME,
        showTitlesColumn: false,
        reverseParser: undefined,
        tableData,
        columns: hasLoanValues ? WATERFALL_FUND_SUMMARY_COLUMNS_WITH_LOAN : WATERFALL_FUND_SUMMARY_COLUMNS,
        rowConfig,
        parser: waterfallFundSummaryParser,
      } as unknown as FundSummaryConfigParams;

      return new SpreadsheetConfig<
        object,
        WaterfallFundSummaryColumn,
        WaterfallFundSummaryTableData,
        AggregatedFundOwnershipDetail
      >(configParams);
    }
    return null;
  }, [rowConfig, tableData, hasLoanValues]);

  // Whether the loan cell is in the workbook and safe to depend on. An unresolved
  // symbol invalidates the referencing cell, and with no data rows between the header
  // and totals rows the totals SUM references itself, leaving it permanently
  // unevaluated — which would stall every cell downstream of it too.
  const hasFirmTotalLoanValueCell = Boolean(sheetConfig) && hasLoanValues && rowConfig.length > 2;

  return {
    sheetConfig,
    hasFirmTotalLoanValueCell,
  };
};
